import React, { useContext, useState } from "react";
import { UserContext } from "./app/context/UserContext";

// Hardcoded password
const CORRECT_PASSWORD = "facial123";

interface DetailedComparison {
    similarity_score: string;
    differences: string;
}

interface Analysis {
    overall_similarity: string;
    detailed_comparison: {
        hair: DetailedComparison;
        eyes: DetailedComparison;
        eyebrows: DetailedComparison;
        nose: DetailedComparison;
        mouth_and_lips: DetailedComparison;
        jawline_and_chin: DetailedComparison;
    };
}

interface AnalysisResult {
    analysis_number: number;
    result: Analysis;
}

interface ApiResponse {
    success: boolean;
    images_processed: string[];
    analyses: AnalysisResult[];
    average_overall_similarity: string;
    number_of_analyses: number;
}

const ImageComparison: React.FC = () => {
    const { user, setUser } = useContext(UserContext);
    console.log("user", user);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [passwordInput, setPasswordInput] = useState("");
    const [passwordError, setPasswordError] = useState("");

    const [gender, setGender] = useState<"man" | "woman">("man");
    const [image1, setImage1] = useState<File | null>(null);
    const [image2, setImage2] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<ApiResponse | null>(null);
    const [error, setError] = useState<string>("");

    const handleLogin = () => {
        if (passwordInput === CORRECT_PASSWORD) {
            setIsAuthenticated(true);
            setUser("user");

            setPasswordError("");
        } else {
            setPasswordError("Incorrect password");
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            handleLogin();
        }
    };

    const handleImage1Change = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setImage1(e.target.files[0]);
        }
    };

    const handleImage2Change = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setImage2(e.target.files[0]);
        }
    };

    const handleSubmit = async () => {
        if (!image1 || !image2) {
            setError("Please select both images");
            return;
        }

        setLoading(true);
        setError("");
        setResult(null);

        const formData = new FormData();
        formData.append("image1", image1);
        formData.append("image2", image2);

        // Choose endpoint based on gender selection
        const endpoint =
            gender === "man"
                ? "https://frc-server.dev.torsunov.pro/analyze-images"
                : "https://frc-server.dev.torsunov.pro/analyze-images-woman";

        try {
            const response = await fetch(endpoint, {
                method: "POST",
                body: formData,
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data: ApiResponse = await response.json();
            setResult(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : "An error occurred");
        } finally {
            setLoading(false);
        }
    };

    const renderAnalysis = (analysis: AnalysisResult) => {
        const { result } = analysis;
        return (
            <div
                key={analysis.analysis_number}
                style={{
                    marginBottom: "30px",
                    padding: "20px",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "8px",
                }}
            >
                <h3 style={{ color: "#2c3e50", marginBottom: "15px" }}>
                    Analysis {analysis.analysis_number} - Overall Similarity:{" "}
                    {result.overall_similarity}
                </h3>

                {Object.entries(result.detailed_comparison).map(
                    ([feature, details]) => (
                        <div
                            key={feature}
                            style={{
                                marginBottom: "15px",
                                paddingLeft: "15px",
                                borderLeft: "3px solid #3498db",
                            }}
                        >
                            <h4
                                style={{
                                    color: "#34495e",
                                    textTransform: "capitalize",
                                    marginBottom: "5px",
                                }}
                            >
                                {feature.replace("_", " & ")} -{" "}
                                {details.similarity_score}
                            </h4>
                            <p style={{ color: "#555", lineHeight: "1.6" }}>
                                {details.differences}
                            </p>
                        </div>
                    ),
                )}
            </div>
        );
    };

    // Login screen
    if (!isAuthenticated) {
        return (
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "100vh",
                    backgroundColor: "#f5f5f5",
                    fontFamily: "system-ui, -apple-system, sans-serif",
                }}
            >
                <div
                    style={{
                        backgroundColor: "white",
                        padding: "40px",
                        borderRadius: "8px",
                        boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                        width: "100%",
                        maxWidth: "400px",
                    }}
                >
                    <h2
                        style={{
                            textAlign: "center",
                            color: "#2c3e50",
                            marginBottom: "30px",
                        }}
                    >
                        Login Required
                    </h2>
                    <div style={{ marginBottom: "20px" }}>
                        <label
                            style={{
                                display: "block",
                                marginBottom: "8px",
                                fontWeight: "600",
                                color: "#34495e",
                            }}
                        >
                            Password:
                        </label>
                        <input
                            type="password"
                            value={passwordInput}
                            onChange={(e) => setPasswordInput(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Enter password"
                            style={{
                                width: "100%",
                                padding: "12px",
                                border: "2px solid #ddd",
                                borderRadius: "4px",
                                fontSize: "16px",
                                boxSizing: "border-box",
                            }}
                        />
                    </div>
                    {passwordError && (
                        <div
                            style={{
                                padding: "10px",
                                backgroundColor: "#fee",
                                color: "#c33",
                                borderRadius: "4px",
                                marginBottom: "20px",
                                fontSize: "14px",
                            }}
                        >
                            {passwordError}
                        </div>
                    )}
                    <button
                        onClick={handleLogin}
                        style={{
                            width: "100%",
                            padding: "12px",
                            backgroundColor: "#3498db",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            fontSize: "16px",
                            fontWeight: "600",
                            cursor: "pointer",
                        }}
                    >
                        Login
                    </button>
                </div>
            </div>
        );
    }

    // Main application screen
    return (
        <div
            style={{
                maxWidth: "900px",
                margin: "0 auto",
                padding: "20px",
                fontFamily: "system-ui, -apple-system, sans-serif",
            }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "30px",
                }}
            >
                <h1 style={{ color: "#2c3e50" }}>Facial Comparison Analysis</h1>
                <button
                    onClick={() => {
                        setIsAuthenticated(false);
                        setUser("");
                    }}
                    style={{
                        padding: "8px 16px",
                        backgroundColor: "#e74c3c",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        fontSize: "14px",
                        cursor: "pointer",
                    }}
                >
                    Logout
                </button>
            </div>

            <div style={{ marginBottom: "30px" }}>
                <div
                    style={{
                        marginBottom: "25px",
                        padding: "20px",
                        backgroundColor: "#f8f9fa",
                        borderRadius: "8px",
                    }}
                >
                    <label
                        style={{
                            display: "block",
                            marginBottom: "12px",
                            fontWeight: "600",
                            color: "#34495e",
                            fontSize: "16px",
                        }}
                    >
                        Select Gender:
                    </label>
                    <div style={{ display: "flex", gap: "20px" }}>
                        <label
                            style={{
                                display: "flex",
                                alignItems: "center",
                                cursor: "pointer",
                            }}
                        >
                            <input
                                type="radio"
                                name="gender"
                                value="man"
                                checked={gender === "man"}
                                onChange={(e) =>
                                    setGender(e.target.value as "man" | "woman")
                                }
                                style={{
                                    marginRight: "8px",
                                    cursor: "pointer",
                                    width: "18px",
                                    height: "18px",
                                }}
                            />
                            <span
                                style={{ fontSize: "16px", color: "#34495e" }}
                            >
                                Man
                            </span>
                        </label>
                        <label
                            style={{
                                display: "flex",
                                alignItems: "center",
                                cursor: "pointer",
                            }}
                        >
                            <input
                                type="radio"
                                name="gender"
                                value="woman"
                                checked={gender === "woman"}
                                onChange={(e) =>
                                    setGender(e.target.value as "man" | "woman")
                                }
                                style={{
                                    marginRight: "8px",
                                    cursor: "pointer",
                                    width: "18px",
                                    height: "18px",
                                }}
                            />
                            <span
                                style={{ fontSize: "16px", color: "#34495e" }}
                            >
                                Woman
                            </span>
                        </label>
                    </div>
                </div>

                <div style={{ marginBottom: "20px" }}>
                    <label
                        style={{
                            display: "block",
                            marginBottom: "8px",
                            fontWeight: "600",
                            color: "#34495e",
                        }}
                    >
                        Image 1:
                    </label>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleImage1Change}
                        style={{
                            padding: "10px",
                            border: "2px solid #ddd",
                            borderRadius: "4px",
                            width: "100%",
                        }}
                    />
                    {image1 && (
                        <p
                            style={{
                                marginTop: "5px",
                                color: "#666",
                                fontSize: "14px",
                            }}
                        >
                            Selected: {image1.name}
                        </p>
                    )}
                </div>

                <div style={{ marginBottom: "20px" }}>
                    <label
                        style={{
                            display: "block",
                            marginBottom: "8px",
                            fontWeight: "600",
                            color: "#34495e",
                        }}
                    >
                        Image 2:
                    </label>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleImage2Change}
                        style={{
                            padding: "10px",
                            border: "2px solid #ddd",
                            borderRadius: "4px",
                            width: "100%",
                        }}
                    />
                    {image2 && (
                        <p
                            style={{
                                marginTop: "5px",
                                color: "#666",
                                fontSize: "14px",
                            }}
                        >
                            Selected: {image2.name}
                        </p>
                    )}
                </div>

                <button
                    onClick={handleSubmit}
                    disabled={loading || !image1 || !image2}
                    style={{
                        padding: "12px 24px",
                        backgroundColor: loading ? "#95a5a6" : "#3498db",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        fontSize: "16px",
                        fontWeight: "600",
                        cursor: loading ? "not-allowed" : "pointer",
                        width: "100%",
                    }}
                >
                    {loading ? "Analyzing..." : "Compare Images"}
                </button>
            </div>

            {error && (
                <div
                    style={{
                        padding: "15px",
                        backgroundColor: "#fee",
                        color: "#c33",
                        borderRadius: "4px",
                        marginBottom: "20px",
                    }}
                >
                    Error: {error}
                </div>
            )}

            {result && (
                <div>
                    <div
                        style={{
                            padding: "20px",
                            backgroundColor: "#e8f5e9",
                            borderRadius: "8px",
                            marginBottom: "30px",
                            textAlign: "center",
                        }}
                    >
                        <h2 style={{ color: "#2e7d32", marginBottom: "10px" }}>
                            Average Overall Similarity:{" "}
                            {result.average_overall_similarity}
                        </h2>
                        <p style={{ color: "#666" }}>
                            Based on {result.number_of_analyses} analyses
                        </p>
                    </div>

                    <div>{result.analyses.map(renderAnalysis)}</div>
                </div>
            )}
        </div>
    );
};

export default ImageComparison;
